import { nullableArray, nullableOptional, toSearchParams } from '@shigo-ui/api-client';
import { DashboardTableStatusTone } from '@shigo-ui/dashboard-ui';
import { z } from 'zod';

import { integrationSchema, IntegrationStatus } from '../integrations/model';
import { demandPartnerSchema } from '../partners/model';
import { targetingBuilderSchema } from '../../targeting/builder/model';

export enum DealStatus {
  Active = 'ACTIVE',
  Inactive = 'INACTIVE',
  Archived = 'ARCHIVED',
}

export enum DealAuctionType {
  FirstPrice = 'FIRST_PRICE',
  SecondPrice = 'SECOND_PRICE',
  BidFloorPrice = 'BID_FLOOR_PRICE',
}

export enum DealFrequencyUnit {
  Hour = 'HOUR',
  Day = 'DAY',
}

export enum DealEditorTab {
  General = 'GENERAL',
  Targeting = 'TARGETING',
  DemandIntegrations = 'DEMAND_INTEGRATIONS',
}

export enum DealSortProperty {
  Name = 'name',
  CreatedOn = 'createdOn',
}

export enum DealSortDirection {
  Asc = 'ASC',
  Desc = 'DESC',
}

const idSchema = z.union([z.string(), z.number()]).transform(String);
const statusSchema = z.preprocess(
  (value) =>
    Object.values(DealStatus).includes(value as DealStatus) ? value : DealStatus.Inactive,
  z.enum(DealStatus),
);
const auctionTypeSchema = z.preprocess(
  (value) =>
    Object.values(DealAuctionType).includes(value as DealAuctionType)
      ? value
      : DealAuctionType.FirstPrice,
  z.enum(DealAuctionType),
);
const frequencyUnitSchema = z.preprocess(
  (value) =>
    Object.values(DealFrequencyUnit).includes(value as DealFrequencyUnit)
      ? value
      : DealFrequencyUnit.Hour,
  z.enum(DealFrequencyUnit),
);

export const dealIntegrationSchema = integrationSchema.extend({
  demandPartner: nullableOptional(demandPartnerSchema),
  dpDisplayName: nullableOptional(z.string()),
  duplication: nullableOptional(z.boolean()),
  sellerDomain: nullableOptional(z.string()),
  revenueLast7Days: nullableOptional(z.number()),
  adRequestsLast7Days: nullableOptional(z.number()),
  stats: nullableOptional(z.looseObject({ global7daysRevenuePct: nullableOptional(z.number()) })),
});

export const dealFrequencyCappingSchema = z.looseObject({
  fcActive: z.boolean().nullish().transform(Boolean),
  fcDuration: nullableOptional(z.number()),
  fcLimit: nullableOptional(z.number()),
  fcTimeUnit: frequencyUnitSchema,
});

export const dealSchema = z.looseObject({
  id: idSchema,
  dealId: z.string().optional().default(''),
  name: z.string().optional().default(''),
  status: statusSchema,
  startDate: nullableOptional(z.string()),
  endDate: nullableOptional(z.string()),
  auctionType: auctionTypeSchema,
  bidFloor: nullableOptional(z.number()),
  bidFloorCur: z
    .string()
    .nullish()
    .transform((value) => value || 'USD'),
  wseat: nullableArray(z.string()),
  wadomain: nullableArray(z.string()),
  demandIntegration: nullableArray(dealIntegrationSchema),
  targetings: nullableArray(targetingBuilderSchema),
  frequencyCapping: z
    .preprocess((value) => value ?? {}, dealFrequencyCappingSchema)
    .default({
      fcActive: false,
      fcDuration: undefined,
      fcLimit: undefined,
      fcTimeUnit: DealFrequencyUnit.Hour,
    }),
  createdOn: nullableOptional(z.string()),
  updatedOn: nullableOptional(z.string()),
});

export const dealsPageSchema = z.looseObject({
  content: z.array(dealSchema).optional().default([]),
  totalElements: z.number().optional().default(0),
  totalPages: z.number().optional().default(0),
  number: z.number().optional().default(0),
  size: z.number().optional().default(0),
});

export const dealStatusCountsSchema = z.preprocess(
  (value) => {
    if (!Array.isArray(value)) return value;
    return Object.fromEntries(
      value
        .filter((item): item is Record<string, unknown> =>
          Boolean(item && typeof item === 'object'),
        )
        .map((item) => [String(item['status']), Number(item['count'] ?? 0)]),
    );
  },
  z
    .object({
      [DealStatus.Active]: z.number().int().nonnegative().default(0),
      [DealStatus.Inactive]: z.number().int().nonnegative().default(0),
      [DealStatus.Archived]: z.number().int().nonnegative().default(0),
    })
    .partial(),
);

export type Deal = z.infer<typeof dealSchema>;
export type DealIntegration = z.infer<typeof dealIntegrationSchema>;
export type DealTargeting = z.infer<typeof targetingBuilderSchema>;
export type DealsPage = z.infer<typeof dealsPageSchema>;

export interface DealsFilter {
  searchTerm: string;
  statuses: DealStatus[];
  page: number;
  size: number;
  sortProperty: DealSortProperty | null;
  sortDirection: DealSortDirection;
}

export interface DealLookupFilter {
  searchTerm: string;
  page: number;
  size: number;
}

export interface DealIntegrationLookupFilter extends DealLookupFilter {
  statuses: IntegrationStatus[];
}

export const dealPageSizes = [5, 10, 20, 25, 50, 100];
export const defaultDealsFilter: DealsFilter = {
  searchTerm: '',
  statuses: [DealStatus.Active],
  page: 0,
  size: 20,
  sortProperty: DealSortProperty.CreatedOn,
  sortDirection: DealSortDirection.Desc,
};
export const defaultDealLookupFilter: DealLookupFilter = {
  searchTerm: '',
  page: 0,
  size: 10,
};
export const defaultDealIntegrationLookupFilter: DealIntegrationLookupFilter = {
  ...defaultDealLookupFilter,
  size: 5,
  statuses: [IntegrationStatus.Active],
};

export const dealFormSchema = z
  .object({
    name: z.string().trim().min(1, 'Name is required.'),
    dealId: z.string().trim().min(1, 'Deal ID is required.'),
    status: z.enum(DealStatus),
    startDate: z.string(),
    endDate: z.string(),
    auctionType: z.enum(DealAuctionType),
    bidFloor: z.string().refine(isOptionalNonNegativeNumber, 'Bid floor must be zero or greater.'),
    bidFloorCur: z.string(),
    wseat: z.string(),
    wadomain: z.string(),
    targetings: z.array(z.custom<DealTargeting>()),
    demandIntegration: z.array(z.custom<DealIntegration>()),
    frequencyCapping: z.object({
      fcActive: z.boolean(),
      fcDuration: z
        .string()
        .refine(isOptionalNonNegativeNumber, 'Duration must be zero or greater.'),
      fcLimit: z.string().refine(isOptionalNonNegativeNumber, 'Limit must be zero or greater.'),
      fcTimeUnit: z.enum(DealFrequencyUnit),
    }),
  })
  .superRefine((value, context) => {
    if (value.startDate && value.endDate && value.startDate > value.endDate) {
      context.addIssue({
        code: 'custom',
        path: ['endDate'],
        message: 'End date must be on or after the start date.',
      });
    }
    if (!value.frequencyCapping.fcActive) return;
    if (!value.frequencyCapping.fcLimit.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['frequencyCapping', 'fcLimit'],
        message: 'Limit is required when frequency capping is enabled.',
      });
    }
    if (!value.frequencyCapping.fcDuration.trim()) {
      context.addIssue({
        code: 'custom',
        path: ['frequencyCapping', 'fcDuration'],
        message: 'Duration is required when frequency capping is enabled.',
      });
    }
  });

export type DealFormValue = z.infer<typeof dealFormSchema>;
export type DealMutationPayload = ReturnType<typeof dealFormValueToPayload>;

export const emptyDealFormValue: DealFormValue = {
  name: '',
  dealId: '',
  status: DealStatus.Active,
  startDate: '',
  endDate: '',
  auctionType: DealAuctionType.FirstPrice,
  bidFloor: '',
  bidFloorCur: 'USD',
  wseat: '',
  wadomain: '',
  targetings: [],
  demandIntegration: [],
  frequencyCapping: {
    fcActive: false,
    fcDuration: '',
    fcLimit: '',
    fcTimeUnit: DealFrequencyUnit.Hour,
  },
};

export function dealToFormValue(deal: Deal): DealFormValue {
  return {
    name: deal.name,
    dealId: deal.dealId,
    status: deal.status,
    startDate: toDateInput(deal.startDate),
    endDate: toDateInput(deal.endDate),
    auctionType: deal.auctionType,
    bidFloor: numberInput(deal.bidFloor),
    bidFloorCur: deal.bidFloorCur,
    wseat: deal.wseat.join('\n'),
    wadomain: deal.wadomain.join('\n'),
    targetings: deal.targetings,
    demandIntegration: deal.demandIntegration,
    frequencyCapping: {
      fcActive: deal.frequencyCapping.fcActive,
      fcDuration: numberInput(deal.frequencyCapping.fcDuration),
      fcLimit: numberInput(deal.frequencyCapping.fcLimit),
      fcTimeUnit: deal.frequencyCapping.fcTimeUnit,
    },
  };
}

export function dealFormValueToPayload(value: DealFormValue, original?: Deal) {
  return {
    ...original,
    name: value.name.trim(),
    dealId: value.dealId.trim(),
    status: value.status,
    startDate: toApiDate(value.startDate),
    endDate: toApiDate(value.endDate),
    auctionType: value.auctionType,
    bidFloor: optionalNumber(value.bidFloor),
    bidFloorCur: value.bidFloorCur || 'USD',
    wseat: parseList(value.wseat),
    wadomain: parseList(value.wadomain),
    targetings: value.targetings,
    demandIntegration: value.demandIntegration,
    frequencyCapping: {
      ...original?.frequencyCapping,
      fcActive: value.frequencyCapping.fcActive,
      fcDuration: optionalNumber(value.frequencyCapping.fcDuration),
      fcLimit: optionalNumber(value.frequencyCapping.fcLimit),
      fcTimeUnit: value.frequencyCapping.fcTimeUnit,
    },
  };
}

export function serializeDealsFilter(filter: DealsFilter) {
  return toSearchParams({
    name: filter.searchTerm.trim() || undefined,
    status: filter.statuses.length ? filter.statuses : undefined,
    page: filter.page,
    size: filter.size,
    ...(filter.sortProperty
      ? { sortProperty: filter.sortProperty, sortDirection: filter.sortDirection }
      : {}),
  }).toString();
}

export function serializeDealLookupFilter(filter: DealLookupFilter) {
  return toSearchParams({
    searchTerm: filter.searchTerm.trim() || undefined,
    page: filter.page,
    size: filter.size,
    sortProperty: 'name',
    sortDirection: DealSortDirection.Asc,
  });
}

export function dealStatusLabel(status: DealStatus) {
  return status.charAt(0) + status.slice(1).toLowerCase();
}

export function dealStatusTone(status: DealStatus) {
  if (status === DealStatus.Active) return DashboardTableStatusTone.Success;
  if (status === DealStatus.Inactive) return DashboardTableStatusTone.Info;
  return DashboardTableStatusTone.Neutral;
}

export function dealAuctionTypeLabel(type: DealAuctionType) {
  if (type === DealAuctionType.FirstPrice) return 'First price';
  if (type === DealAuctionType.SecondPrice) return 'Second price';
  return 'Bid floor price';
}

export function isDealSortProperty(value: string): value is DealSortProperty {
  return Object.values(DealSortProperty).includes(value as DealSortProperty);
}

function isOptionalNonNegativeNumber(value: string) {
  return !value.trim() || (Number.isFinite(Number(value)) && Number(value) >= 0);
}

function optionalNumber(value: string) {
  return value.trim() ? Number(value) : null;
}

function numberInput(value: number | null | undefined) {
  return value === null || value === undefined ? '' : String(value);
}

function parseList(value: string) {
  return value
    .split(/[\n,]/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function toDateInput(value: string | null | undefined) {
  return value ? value.slice(0, 10) : '';
}

function toApiDate(value: string) {
  return value ? `${value}T00:00:00.000Z` : null;
}
